import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AdminAuth = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingSession, setLoadingSession] = useState(true); // Wait until session is verified

  useEffect(() => {
    const verifyAdminSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const userEmail = data.session?.user?.email;

        if (userEmail) {
          const { data: adminRecord, error: adminError } = await supabase
            .from('admin_users')
            .select('*')
            .eq('email', userEmail)
            .eq('role', 'admin')
            .single();

          if (adminRecord && !adminError) {
            onLogin(true);
          } else {
            await supabase.auth.signOut();
            onLogin(false);
          }
        } else {
          onLogin(false);
        }
      } catch (err) {
        console.error('Admin session verification failed:', err);
        onLogin(false);
      } finally {
        setLoadingSession(false); // Session check complete
      }
    };

    verifyAdminSession();

    // Listen to auth changes across tabs
    const { data: listener } = supabase.auth.onAuthStateChange(() => {
      verifyAdminSession();
    });

    return () => listener.subscription.unsubscribe();
  }, [onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.username,
        password: credentials.password,
      });

      if (signInError || !data.user) {
        setError('Invalid email or password');
        return;
      }

      const { data: adminRecord, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', credentials.username)
        .eq('role', 'admin')
        .single();

      if (adminError || !adminRecord) {
        await supabase.auth.signOut();
        setError('Access denied. Admin privileges required.');
        return;
      }

      onLogin(true);
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  // Don't render login form until session is verified
  if (loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-lg text-slate-700">Loading...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-blue via-blue-800 to-royal-blue flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
          <h1 className="text-3xl font-bold text-slate-800 mb-4 text-center">Admin Portal</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                placeholder="Enter admin email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-royal-blue focus:border-transparent"
                placeholder="Enter password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-royal-blue to-blue-800 text-white px-8 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;
